import { useEffect, useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { HomeScreen } from "./screens/HomeScreen";
import { ProcessingScreen } from "./screens/ProcessingScreen";
import { ReviewScreen } from "./screens/ReviewScreen";
import { EditorScreen } from "./screens/EditorScreen";
import { SettingsScreen } from "./screens/SettingsScreen";
import { flushProjectSave, useAppStore } from "./state/appStore";
import { recognizeFile } from "./recognition/client";
import { projectRepository } from "./core/database";
import { logDiagnostic } from "./core/diagnostics";

export default function App() {
  const { screen, setScreen, processingError, setProcessing, setProcessingError, setRecognition, toast, setToast } = useAppStore();
  const [lastFile, setLastFile] = useState<File | null>(null);
  const controller = useRef<AbortController | null>(null);
  const { needRefresh: [needRefresh], updateServiceWorker } = useRegisterSW({ immediate: true });

  useEffect(() => {
    const id = localStorage.getItem("pindou:last-project");
    if (!id) return;
    void projectRepository.get(id).then((activeProject) => {
      if (activeProject) useAppStore.setState({ activeProject, screen: "editor", undoStack: [], redoStack: [] });
      else localStorage.removeItem("pindou:last-project");
    });
  }, []);

  useEffect(() => {
    const flush = () => { void flushProjectSave(); };
    document.addEventListener("visibilitychange", flush);
    window.addEventListener("pagehide", flush);
    return () => { document.removeEventListener("visibilitychange", flush); window.removeEventListener("pagehide", flush); };
  }, []);
  useEffect(() => { if (!toast) return; const timer = setTimeout(() => setToast(null), 2600); return () => clearTimeout(timer); }, [setToast, toast]);

  async function importFile(file: File, expected?: { width: number; height: number }) {
    controller.current?.abort();
    controller.current = new AbortController();
    setLastFile(file);
    setScreen("processing");
    setProcessingError(null);
    setProcessing({ stage: "准备图片", ratio: .02, detail: "正在安全读取图片…" });
    logDiagnostic("info", "recognition-start", `${file.type || "unknown"} · ${file.size} bytes`);
    let wakeLock: { release: () => Promise<void> } | null = null;
    try {
      const wake = (navigator as Navigator & { wakeLock?: { request: (type: "screen") => Promise<{ release: () => Promise<void> }> } }).wakeLock;
      if (wake) {
        try { wakeLock = await wake.request("screen"); }
        catch { setToast("无法保持屏幕常亮，识别时请暂勿锁屏"); }
      }
      const result = await recognizeFile(file, { expectedWidth: expected?.width, expectedHeight: expected?.height }, setProcessing, controller.current.signal);
      setRecognition(result);
      setScreen("review");
      logDiagnostic("info", "recognition-complete", `${result.project.width}x${result.project.height} · ${result.totalFromCells} cells · ${result.issues.length} issues`);
    } catch (error) {
      if (controller.current.signal.aborted) { setScreen("home"); return; }
      const message = error instanceof Error ? error.message : typeof error === "string" ? error : JSON.stringify(error);
      setProcessingError(message && message !== "{}" ? message : "识别过程中出现未知错误");
      logDiagnostic("error", "recognition-failed", message && message !== "{}" ? message : "unknown error");
    } finally {
      await wakeLock?.release().catch(() => undefined);
    }
  }

  function cancel() { controller.current?.abort(); setProcessing(null); setProcessingError(null); setScreen("home"); }

  return <div className="app-shell">
    {screen === "home" && <HomeScreen onImport={(file) => void importFile(file)} />}
    {screen === "processing" && <ProcessingScreen onCancel={cancel} onRetry={() => lastFile && void importFile(lastFile)} />}
    {screen === "review" && <ReviewScreen onBack={cancel} onReprocess={(width, height) => lastFile && void importFile(lastFile, { width, height })} />}
    {screen === "editor" && <EditorScreen />}
    {screen === "settings" && <SettingsScreen />}
    {toast && <div className="toast" role="status">{toast}</div>}
    {needRefresh && <div className="update-banner"><span>发现新版本，项目已安全保存。</span><button onClick={() => { void flushProjectSave().then(() => updateServiceWorker(true)); }}>立即更新</button></div>}
    {processingError && screen !== "processing" && null}
  </div>;
}
